import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Center, Loader } from "@mantine/core";
import { AuthProvider } from "./contexts/AuthContext.js";
import { Layout } from "./components/Layout.js";
import { ProtectedRoute } from "./components/ProtectedRoute.js";
import { LoginPage } from "./pages/LoginPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { ApplicationsPage, ApplicationDetailPage } from "./pages/ApplicationsPage.js";
import { PortalFeatureGuard } from "./components/PortalFeatureGuard.js";
const FeaturesPage = lazy(() =>
  import("./pages/FeaturesPage.js").then((m) => ({ default: m.FeaturesPage })),
);
const DiscordAuditLogsPage = lazy(() =>
  import("./pages/DiscordAuditLogsPage.js").then((m) => ({ default: m.DiscordAuditLogsPage })),
);
const UsersPage = lazy(() =>
  import("./pages/UsersPage.js").then((m) => ({ default: m.UsersPage })),
);
const DaRulesPage = lazy(() =>
  import("./pages/DaRulesPage.js").then((m) => ({ default: m.DaRulesPage })),
);
const MyProfilePage = lazy(() =>
  import("./pages/MyProfilePage.js").then((m) => ({ default: m.MyProfilePage })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage.js").then((m) => ({ default: m.SettingsPage })),
);
const DisciplinePage = lazy(() =>
  import("./pages/DisciplinePage.js").then((m) => ({ default: m.DisciplinePage })),
);

const MembersPage = lazy(() =>
  import("./pages/MembersPage.js").then((m) => ({ default: m.MembersPage })),
);
const MemberDetailPage = lazy(() =>
  import("./pages/MemberDetailPage.js").then((m) => ({ default: m.MemberDetailPage })),
);
const MemberByWebsiteIdRedirect = lazy(() =>
  import("./pages/MemberByWebsiteIdRedirect.js").then((m) => ({
    default: m.MemberByWebsiteIdRedirect,
  })),
);
const RoleMappingsPage = lazy(() =>
  import("./pages/RoleMappingsPage.js").then((m) => ({ default: m.RoleMappingsPage })),
);
const AuditLogPage = lazy(() =>
  import("./pages/AuditLogPage.js").then((m) => ({ default: m.AuditLogPage })),
);
const CompliancePage = lazy(() =>
  import("./pages/CompliancePage.js").then((m) => ({ default: m.CompliancePage })),
);
const ServerPlayersPage = lazy(() => import("./pages/ServerPlayersPage.js"));
const AdminRequestsPage = lazy(() =>
  import("./pages/AdminRequestsPage.js").then((m) => ({ default: m.AdminRequestsPage })),
);
const AdminRequestFormPage = lazy(() =>
  import("./pages/AdminRequestFormPage.js").then((m) => ({ default: m.AdminRequestFormPage })),
);
const AdminRequestDetailPage = lazy(() =>
  import("./pages/AdminRequestDetailPage.js").then((m) => ({ default: m.AdminRequestDetailPage })),
);
const PendingApprovalsPage = lazy(() =>
  import("./pages/PendingApprovalsPage.js").then((m) => ({ default: m.PendingApprovalsPage })),
);
const MyRequestsPage = lazy(() =>
  import("./pages/MyRequestsPage.js").then((m) => ({ default: m.MyRequestsPage })),
);
const CommunityPage = lazy(() =>
  import("./pages/CommunityPage.js").then((m) => ({ default: m.CommunityPage })),
);
const HubPermissionsPage = lazy(() =>
  import("./pages/HubPermissionsPage.js").then((m) => ({ default: m.HubPermissionsPage })),
);
const FeedbackPage = lazy(() =>
  import("./pages/FeedbackPage.js").then((m) => ({ default: m.FeedbackPage })),
);
const FeedbackBoardPage = lazy(() =>
  import("./pages/FeedbackBoardPage.js").then((m) => ({ default: m.FeedbackBoardPage })),
);
const FeedbackPostPage = lazy(() =>
  import("./pages/FeedbackPostPage.js").then((m) => ({ default: m.FeedbackPostPage })),
);

const PageFallback = () => (
  <Center h="60vh">
    <Loader size="sm" />
  </Center>
);

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* Protected shell */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route
                path="applications"
                element={
                  <PortalFeatureGuard feature="applications">
                    <ApplicationsPage />
                  </PortalFeatureGuard>
                }
              />
              <Route
                path="applications/:id"
                element={
                  <PortalFeatureGuard feature="applications">
                    <ApplicationDetailPage />
                  </PortalFeatureGuard>
                }
              />
              <Route
                path="admin/users"
                element={
                  <ProtectedRoute permissionKey="users.view">
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/da-rules"
                element={
                  <ProtectedRoute permissionKey="da_rules.manage">
                    <DaRulesPage />
                  </ProtectedRoute>
                }
              />
              <Route path="my-profile" element={<MyProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route
                path="discipline/search"
                element={
                  <ProtectedRoute permissionKey="da.view">
                    <DisciplinePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="discipline/recent"
                element={<Navigate to="/discipline/search" replace />}
              />
              <Route
                path="members"
                element={
                  <ProtectedRoute permissionKey="member.view_list">
                    <MembersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="members/by-website-id/:websiteId"
                element={
                  <ProtectedRoute permissionKey="member.view_list">
                    <MemberByWebsiteIdRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="members/:id"
                element={
                  <ProtectedRoute permissionKey="member.view_list">
                    <MemberDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="role-mappings"
                element={
                  <ProtectedRoute permissionKey="role_mappings.manage">
                    <RoleMappingsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="feedback/:boardSlug" element={<FeedbackBoardPage />} />
              <Route path="feedback/:boardSlug/:postId" element={<FeedbackPostPage />} />
              <Route
                path="admin/features"
                element={
                  <ProtectedRoute permissionKey="portal_features.manage">
                    <FeaturesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/discord-logs"
                element={
                  <ProtectedRoute permissionKey="discord_logs.manage">
                    <DiscordAuditLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="community" element={<CommunityPage />} />
              <Route
                path="admin/server"
                element={
                  <ProtectedRoute permissionKey="server_players.manage">
                    <ServerPlayersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/compliance"
                element={
                  <ProtectedRoute permissionKey="compliance.view">
                    <CompliancePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-requests"
                element={
                  <ProtectedRoute>
                    <MyRequestsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/requests"
                element={
                  <ProtectedRoute permissionKey="admin_request.view">
                    <AdminRequestsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/requests/new"
                element={
                  <ProtectedRoute permissionKey="admin_request.submit">
                    <AdminRequestFormPage />
                  </ProtectedRoute>
                }
              />{" "}
              <Route
                path="admin/requests/:id"
                element={
                  <ProtectedRoute permissionKey="admin_request.view">
                    <AdminRequestDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/pending-approvals"
                element={
                  <ProtectedRoute permissionKey="da_update.request">
                    <PendingApprovalsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/audit-log"
                element={
                  <ProtectedRoute permissionKey="audit_log.view">
                    <AuditLogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/hub-permissions"
                element={
                  <ProtectedRoute sysadminOnly>
                    <HubPermissionsPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
